import { EDatasourceType } from "@kuika/kuika-cl-model";

declare let window: Window & { userInfo?: { username?: string } };

/**
 * Users allowed to see the Kuika-internal AI previews (AI Agent, Vision Action).
 * Compared case-insensitively against the signed-in username.
 */
const KUIKA_INTERNAL_AI_FEATURE_USERS: ReadonlySet<string> = new Set([
  "ilyas.akin@kuika.com",
  "sergen.gocmen@kuika.com",
  "tolga.ozkan@kuika.com",
  "sergen.gocmen+20@kuika.com",
  "furkan.ceylan@kuika.com",
  "burcu.yazici@kuika.com",
  "kadir.bayar@kuika.com"
]);

/**
 * Single source of truth for per-user feature access checks in the designer.
 *
 * Every call site expresses intent in product terms (e.g. "can this user see AI
 * Agents?") rather than re-deriving the rule inline. Gating rules can evolve
 * here (role-based access, feature flags, etc.) without touching call sites.
 */
export class FeatureGates {
  private static getCurrentUsername(): string {
    return window?.userInfo?.username ?? "";
  }

  /**
   * AI Agent and Vision Action are currently Kuika-internal previews, restricted
   * to the explicit user allowlist above. Other users — including other
   * `@kuika.com` accounts — must not see entry points for these features, nor
   * records that reference them.
   */
  static canAccessKuikaInternalAiFeatures(username: string = FeatureGates.getCurrentUsername()): boolean {
    return KUIKA_INTERNAL_AI_FEATURE_USERS.has(username.trim().toLowerCase());
  }

  /**
   * Returns true if the given AI-family datasource type is visible to the current
   * user. AI Actions are available to everyone; AI Agent and Vision Action are
   * gated behind `canAccessKuikaInternalAiFeatures`.
   */
  static canAccessAiDatasourceType(
    type: EDatasourceType,
    username: string = FeatureGates.getCurrentUsername()
  ): boolean {
    if (type === EDatasourceType.AIAgent || type === EDatasourceType.VisionAction) {
      return FeatureGates.canAccessKuikaInternalAiFeatures(username);
    }
    return true;
  }

  /**
   * Kill switch for the process-template gallery. The catalog is backend content
   * (`GET /v1/api/workflow/templates` ← Metadatas/process-templates.json, editable
   * without a redeploy) and "Use Template" instantiates a process from an entry's
   * json/xml pair. When off, the gallery tab stays enterable but carries a
   * "Coming soon" pill and lands on a notice instead of the cards.
   */
  static isProcessTemplatesEnabled(): boolean {
    return true;
  }
}
